import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { App as AntdApp, Button, Tag } from 'antd';

import ContextBar from '../components/common/ContextBar';
import EmptyState from '../components/common/EmptyState';
import { RecordTitle } from '../components/common/RecordBadge';
import PublishedBy from '../components/publishers/PublishedBy';
import PackageCollectionModal from '../components/publishing/PackageCollectionModal';
import { STATUS_LABELS, builtWords, packageIdentity, packageMetaLine, publishingPackagePath } from '../components/publishing/publishingWords';
import { DataState, Skeleton, deriveDataState } from '../components/loading';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { PACKAGE_KIND_LABELS } from '../services/packages';
import { listPublisherPackages } from '../services/publishing';
import { normalizeId } from '../utils/ids';
import { PLUS } from '../components/icons';

/**
 * /publishing — a third-party publisher's own packages (keepr-api KPR-1331,
 * docs/SCHEMA.md "Publishing: a publisher's own packages"), every status, and
 * the one primary: **Package a collection…** (which says the limit when the
 * publisher holds as many never-listed packages as it may, KPR-1445). Shown to the accounts a
 * publisher names (the account menu's Publishing entry); anyone else who
 * lands here is told what it is for, and the API refuses them anyway.
 */
export default function Publishing() {
	const navigate = useNavigate();
	const { message } = AntdApp.useApp();
	const { user } = useCurrentUser();
	const publisher = user?.publisher || null;
	const [rows, setRows] = useState(null);
	// How many never-listed packages it holds, and the most it may (keepr-api KPR-1445), or null.
	const [drafts, setDrafts] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [refused, setRefused] = useState(null);
	const [building, setBuilding] = useState(false);
	const requestRef = useRef(0);

	const load = useCallback(() => {
		const request = ++requestRef.current;
		setLoading(true);
		setError(null);
		listPublisherPackages()
			.then(({ rows: list, drafts: allowance }) => { if (request === requestRef.current) { setRows(list); setDrafts(allowance || null); setRefused(null); setLoading(false); } })
			.catch((e) => {
				if (request !== requestRef.current) return;
				// Not one of a publisher's accounts (or no longer): not a failure to retry.
				if (e?.response?.status === 403) setRefused(e.response.data?.message || 'Publishing is for the accounts a publisher names.');
				else setError(e);
				setLoading(false);
			});
	}, []);

	useEffect(() => {
		load();
		return () => { requestRef.current += 1; };
	}, [load]);

	const onCreated = (made) => {
		setBuilding(false);
		setDrafts(d => (d ? { ...d, count: d.count + 1 } : d));
		message.success(builtWords(made?.name || 'the package', made?.report));
		navigate(publishingPackagePath(made?._id));
	};

	const hasData = rows !== null;
	const isEmpty = hasData && rows.length === 0;

	return (
		<div className="publishing-page">
			<ContextBar
				title="Publishing"
				below={publisher ? <>Packages you publish on Discover for <PublishedBy publisher={publisher} bare /></> : 'Packages you publish on Discover'}
				actions={refused ? null : (
					<Button type="primary" icon={<PLUS />} onClick={() => setBuilding(true)}>Package a collection…</Button>
				)}
			/>
			{refused ? (
				<div className="list-empty publishing-page__refused"><span>{refused}</span></div>
			) : (
				<DataState
					state={deriveDataState({ loading, error, hasData, isEmpty })}
					hasData={hasData}
					isEmpty={isEmpty}
					skeleton={<Skeleton variant="list-row" count={4} />}
					empty={(
						<EmptyState
							title="Nothing published yet"
							description="Package a collection you manage, then publish it. A reviewer approves each version before Discover lists it."
							primaryAction={{ label: 'Package a collection…', onClick: () => setBuilding(true) }}
							learnMore="discover/publish-a-pack"
						/>
					)}
					error={error ? 'Check your connection and try again.' : null}
					onRetry={load}
					label="your packages"
				>
					<ul className="publishing-list" aria-label="Your packages">
						{(rows || []).map(pkg => (
							<li key={normalizeId(pkg._id)} className="publishing-list__row">
								<Link to={publishingPackagePath(pkg._id)} className="publishing-list__link">
									<RecordTitle kind={pkg.kind === 'collection' ? 'collection' : 'card'} {...packageIdentity(pkg)} title={pkg.name} size="sm" />
									<span className="publishing-list__meta">{packageMetaLine(pkg, PACKAGE_KIND_LABELS)}</span>
								</Link>
								{pkg.waiting ? <Tag className="publishing-list__status">Waiting for review</Tag> : null}
								<Tag className="publishing-list__status">{STATUS_LABELS[pkg.status] || pkg.status}</Tag>
							</li>
						))}
					</ul>
				</DataState>
			)}
			<PackageCollectionModal open={building} publisher={publisher} drafts={drafts} onClose={() => setBuilding(false)} onCreated={onCreated} />
		</div>
	);
}
